import { SimpleGrid } from "@chakra-ui/layout"
import React, { useCallback } from "react"
import styled from "styled-components"

import { LineHeight, Color, SpacingSize, RadiusSize } from "~/consts/style-mixins"

type Item<SOrN extends string | number> = {
  text: string
  value: SOrN
}

export interface Props<SOrN extends string | number> {
  value?: SOrN
  columnNumber?: number
  small?: boolean
  isError?: boolean
  items: Item<SOrN>[]
  onClickItem?: (item: Item<SOrN>) => void
  onChange?: (value: SOrN) => void
}

const RadioButton = <SOrN extends string | number>(props: Props<SOrN>) => {
  const { value, columnNumber = 1, small, isError, items, onClickItem, onChange } = props

  const onClick = (item: Item<SOrN>) => {
    onClickItem?.(item)
    onChange?.(item.value)
  }

  return (
    <SimpleGrid columns={columnNumber} spacing={SpacingSize.XS}>
      {items.map((item) => (
        <StyledRadio
          key={`radio_item_${item.value}`}
          isChecked={item.value === value}
          isError={isError}
          small={small}
          onClick={() => onClick(item)}
        >
          <div className="circle_outer">
            <div className="circle_inner" />
          </div>
          <div className="text">{item.text}</div>
        </StyledRadio>
      ))}
    </SimpleGrid>
  )
}

interface StyledRadioProps {
  small?: boolean
  isChecked: boolean
  isError?: boolean
}

const StyledRadio = styled.div<StyledRadioProps>`
  background: ${({ isChecked, isError }) =>
    isError ? Color.BackgroundAttention : isChecked ? Color.BackgroundGray : Color.White};
  border: 1px solid
    ${({ isChecked, isError }) => (isError ? Color.Attention : isChecked ? Color.Primary : Color.BorderGray)};
  box-sizing: border-box;
  border-radius: ${RadiusSize.Small}px;
  display: flex;
  align-items: center;
  padding: ${({ small }) => (small ? SpacingSize.XS : SpacingSize.S)}px;
  cursor: pointer;
  transition: 0.2s;
  &:hover {
    border: 1px solid ${({ isChecked }) => (isChecked ? Color.Primary : Color.Gray)};
    .circle_outer {
      border: 1px solid ${({ isChecked }) => (isChecked ? Color.Primary : Color.Gray)};
    }
  }
  .circle_outer {
    position: relative;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${Color.White};
    border: 1px solid ${({ isChecked }) => (isChecked ? Color.Primary : Color.LightGray)};
    box-sizing: border-box;
    transition: 0.2s;
  }
  .circle_inner {
    position: absolute;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: ${({ isChecked }) => (isChecked ? Color.Primary : Color.White)};
  }
  .text {
    width: calc(100% - 20px);
    padding-left: ${SpacingSize.XS}px;
    color: ${Color.Black};
    line-height: ${LineHeight.S}%;
  }
`

export default RadioButton
